<template>
  <DropdownMenu v-model:open="isSettingsDropdownOpen">
    <DropdownMenuTrigger
      :class="
        cn(
          'text-accent-400 hover:bg-neutral-50/[0.12] py-2 px-3 inline-flex',
          isSettingsDropdownOpen && 'bg-neutral-50/[0.12]'
        )
      "
    >
      <Menu class="size-6" />
    </DropdownMenuTrigger>
    <DropdownMenuContent class="w-64 py-2">
      <div class="px-2 mb-1">
        <Breadcrumb />
      </div>
      <hr class="h-[1px] bg-neutral-800 border-0 my-1 mx-1" />
      <Protect
        :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_EDIT]"
        :roles="['editor']"
      >
        <Collapsible v-model:open="isSettingsCollapsibleOpen">
          <CollapsibleTrigger as-child>
            <DropdownMenuItem
              class="justify-between text-sm text-neutral-500"
              @select="e => e.preventDefault()"
            >
              <span class="inline-flex gap-2 items-center">
                <Settings class="size-4" /> Project Settings
              </span>
              <ChevronRight
                :class="
                  cn(
                    'size-4 transition-transform',
                    isSettingsCollapsibleOpen && 'rotate-90'
                  )
                "
              />
            </DropdownMenuItem>
          </CollapsibleTrigger>
          <CollapsibleContent class="p-1 border-neutral-800 border rounded">
            <DropdownMenuItem class="justify-between" @select="toggleGrid">
              <span>Show grid</span>
              <Check v-if="grid.show" class="size-4 text-accent-400" />
            </DropdownMenuItem>
            <DropdownMenuItem class="justify-between" @select="snapToGrid">
              <span>Snap to grid</span>
              <Check v-if="grid.snap" class="size-4 text-accent-400" />
            </DropdownMenuItem>
            <DropdownMenuItem
              class="justify-between"
              @select="toggleSpellCheck"
            >
              <span>Spell checking</span>
              <Check v-if="spellcheck.show" class="size-4 text-accent-400" />
            </DropdownMenuItem>
            <DropdownMenuItem
              class="justify-between"
              @select="onPointerDeviceToggle"
            >
              <span>Input device</span>
              <div class="text-sm text-center">
                <template v-if="pointerDeviceAutoDetect">
                  <div>Auto</div>
                </template>
                <small class="text-neutral-500">{{ pointerDeviceMode }}</small>
              </div>
            </DropdownMenuItem>
          </CollapsibleContent>
        </Collapsible>
      </Protect>

      <Protect
        :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_EXPORT]"
        :roles="['editor']"
      >
        <DropdownMenuItem
          class="justify-between text-sm text-neutral-500"
          @select="onExportButton"
        >
          <span class="inline-flex gap-2 items-center">
            <Download class="size-4" /> Export Project
          </span>
          <ChevronRight class="size-4" />
        </DropdownMenuItem>
      </Protect>

      <Protect
        v-if="isParticipant || isLoggedIn"
        :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_READ]"
        :roles="['viewer', 'editor']"
      >
        <DropdownMenuItem
          class="justify-between text-sm text-neutral-500"
          @select="onShareOpen"
        >
          <span class="inline-flex gap-2 items-center">
            <Share2 class="size-4" /> Share Project
          </span>
          <ChevronRight class="size-4" />
        </DropdownMenuItem>
      </Protect>
    </DropdownMenuContent>
  </DropdownMenu>

  <ShareDialog v-model:open="isShareDialogOpen" />
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import {
  Menu,
  Check,
  Share2,
  Settings,
  Download,
  ChevronRight,
} from 'lucide-vue-next';
import { SYS_PERMISSIONS } from '@/constants.js';
import Protect from '@/hoc/Protect.vue';
import { cn } from '@ui/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@ui/components/collapsible';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@ui/components/dropdown-menu';

import ShareDialog from '@/features/projectSharing/ShareDialog.vue';
import { DEVICE_MODE } from '@/lib/pointerDeviceDetector.js';
import Breadcrumb from './Breadcrumb.vue';

const store = useStore();

const isSettingsDropdownOpen = ref(false);
const isSettingsCollapsibleOpen = ref(false);
const isShareDialogOpen = ref(false);

const grid = computed(() => store.getters['project/config/getGrid']);
const spellcheck = computed(
  () => store.getters['project/config/getSpellcheck']
);
const pointerDeviceAutoDetect = computed(
  () => store.getters['application/getPointerDeviceAutoDetect']
);
const pointerDeviceMode = computed(
  () => store.getters['application/getPointerDeviceMode']
);
const isParticipant = computed(
  () => store.getters['project/users/amIParticipant']
);
const isLoggedIn = computed(() => store.getters['user/account/getIsLoggedIn']);

const onShareOpen = () => {
  isShareDialogOpen.value = true;
};

const onExportButton = () => {
  store.dispatch('application/modals/setExportModal', { value: true });
};

const toggleGrid = e => {
  e.preventDefault();
  store.dispatch('project/config/setGrid', {
    props: { show: !grid.value.show },
    apply: true,
  });
};

const snapToGrid = e => {
  e.preventDefault();
  store.dispatch('project/config/setGrid', {
    props: { snap: !grid.value.snap },
    apply: true,
  });
};

const toggleSpellCheck = e => {
  e.preventDefault();
  store.dispatch('project/config/setSpellcheck', {
    props: { show: !spellcheck.value.show },
    apply: true,
  });
};

const onPointerDeviceToggle = e => {
  e.preventDefault();
  if (pointerDeviceAutoDetect.value) {
    store.dispatch('application/setPointerDeviceAutoDetect', { value: false });
    store.dispatch('application/setPointerDeviceMode', {
      value: DEVICE_MODE.MOUSE,
    });
  } else if (pointerDeviceMode.value === DEVICE_MODE.MOUSE) {
    store.dispatch('application/setPointerDeviceMode', {
      value: DEVICE_MODE.TOUCHPAD,
    });
  } else {
    store.dispatch('application/setPointerDeviceAutoDetect', { value: true });
    store.dispatch('application/setPointerDeviceMode', {
      value: DEVICE_MODE.MOUSE,
    });
  }
};
</script>
